import { ThreatBadge } from '../components/ThreatBadge';
import { AlertTriangle, RefreshCw, LayoutGrid, List, Globe, Clock, Activity, Shield, Search, X, Download, Trash2, CheckSquare, Square } from 'lucide-react';
import { useThreatFeed } from '../lib/hooks';
import { useAuth } from '../lib/auth-context';
import { getTechniqueName, getTechniqueDescription, getTechniqueCategory, getTechniqueCategoryColor } from '../lib/mitre-techniques';
import { useState, useMemo } from 'react';
import { type ThreatFeedItem } from '../lib/api';
import { safeDate } from '../lib/validation';
import { EmptyState } from '../components/EmptyState';
import { AttributionBadge } from '../components/AttributionBadge';
import { ThreatCardSkeleton, TableRowSkeleton } from '../components/SkeletonLoader';
import { ThreatDetailPanel } from '../components/ThreatDetailPanel';
import { SgisBadge } from '../components/SgisBadge';
import { ENGAGEMENT_VOCAB } from '../lib/engagement-vocab';

export function LiveThreats() {
  const { isConnected } = useAuth();
  const { data, isLoading, refetch } = useThreatFeed();
  const [viewMode, setViewMode] = useState<'cards' | 'table'>('cards');
  const [searchQuery, setSearchQuery] = useState('');
  const [levelFilter, setLevelFilter] = useState<string>('all');
  const [sourceFilter, setSourceFilter] = useState<string>('all');
  const [selectedThreats, setSelectedThreats] = useState<Set<string>>(new Set());
  const [detailPanelThreat, setDetailPanelThreat] = useState<ThreatFeedItem | null>(null);

  const threats = data?.threats ?? [];

  // Filter threats based on search and filters
  const filteredThreats = useMemo(() => {
    return threats.filter(t => {
      // Search by IP
      if (searchQuery && !t.sourceIp.toLowerCase().includes(searchQuery.toLowerCase())) return false;
      
      // Level filter
      if (levelFilter !== 'all' && t.level !== levelFilter) return false;
      
      // Source filter
      if (sourceFilter === 'tor' && !t.isTor) return false;
      if (sourceFilter === 'cloud' && !t.isCloud) return false;
      if (sourceFilter === 'scanner' && !t.isKnownScanner) return false;
      
      return true;
    });
  }, [threats, searchQuery, levelFilter, sourceFilter]);

  // Bulk action handlers
  const toggleSelectAll = () => {
    if (selectedThreats.size === filteredThreats.length) {
      setSelectedThreats(new Set());
    } else {
      setSelectedThreats(new Set(filteredThreats.map(t => t.id)));
    }
  };

  const exportSelected = () => {
    const selectedData = filteredThreats.filter(t => selectedThreats.has(t.id));
    const dataStr = JSON.stringify(selectedData, null, 2);
    const dataBlob = new Blob([dataStr], { type: 'application/json' });
    const url = URL.createObjectURL(dataBlob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `threats-export-${new Date().toISOString().split('T')[0]}.json`;
    link.click();
    URL.revokeObjectURL(url);
  };

  const clearSelection = () => {
    setSelectedThreats(new Set());
  };

  if (!isConnected) {
    return (
      <div className="p-8">
        <h2 className="text-2xl font-bold text-white mb-8">Live Threats</h2>
        <EmptyState
          icon={AlertTriangle}
          title="Not Connected"
          description="Connect to control plane API to view live threats. Configure API endpoint in Settings."
        />
      </div>
    );
  }

  return (
    <div className="p-8 max-w-7xl mx-auto">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-2xl font-bold text-white">Live Threats</h2>
          <p className="text-gray-400 mt-1">Real-time threat detection and tracking</p>
        </div>
        <div className="flex items-center gap-3">
          {/* View Mode Toggle */}
          <div className="flex bg-gray-800 border border-gray-700 rounded-lg p-1">
            <button
              onClick={() => setViewMode('cards')}
              className={`p-2 rounded transition-colors ${
                viewMode === 'cards' ? 'bg-blue-600 text-white' : 'text-gray-400 hover:text-white'
              }`}
              title="Card View"
            >
              <LayoutGrid className="w-4 h-4" />
            </button>
          <button
            onClick={() => setViewMode('table')}
            className={`p-2 rounded transition-colors hidden md:block ${
              viewMode === 'table' ? 'bg-blue-600 text-white' : 'text-gray-400 hover:text-white'
            }`}
            title="Table View"
          >
            <List className="w-4 h-4" />
          </button>
          </div>

          <button
            onClick={refetch}
            aria-label="Refresh threat feed"
            className="p-2 text-gray-400 hover:text-white transition-colors"
            title="Refresh"
          >
            <RefreshCw className={`w-4 h-4 ${isLoading ? 'animate-spin' : ''}`} />
          </button>

          <div className="flex items-center gap-2 px-3 py-1.5 bg-sg-red/10 border border-sg-red/20 rounded-lg">
            <div className="w-2 h-2 rounded-full bg-sg-red animate-pulse" />
            <span className="text-sg-red text-sm font-medium">Live</span>
          </div>
        </div>
      </div>

      {/* Search and Filters */}
      <div className="mb-6 space-y-4">
        <div className="flex flex-col md:flex-row gap-3">
          {/* Search */}
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
            <input
              type="text"
              placeholder="Search by IP address..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-10 pr-10 py-2.5 bg-sg-card border border-sg-border rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-blue-500/50 transition-colors"
            />
            {searchQuery && (
              <button
                onClick={() => setSearchQuery('')}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-white transition-colors"
              >
                <X className="w-4 h-4" />
              </button>
            )}
          </div>

          {/* Level Filter */}
          <select
            value={levelFilter}
            onChange={(e) => setLevelFilter(e.target.value)}
            className="px-4 py-2.5 bg-sg-card border border-sg-border rounded-lg text-white focus:outline-none focus:border-blue-500/50 transition-colors cursor-pointer"
          >
            <option value="all">All Levels</option>
            <option value="critical">Critical</option>
            <option value="high">High</option>
            <option value="medium">Medium</option>
            <option value="low">Low</option>
          </select>

          {/* Source Filter */}
          <select
            value={sourceFilter}
            onChange={(e) => setSourceFilter(e.target.value)}
            className="px-4 py-2.5 bg-sg-card border border-sg-border rounded-lg text-white focus:outline-none focus:border-blue-500/50 transition-colors cursor-pointer"
          >
            <option value="all">All Sources</option>
            <option value="tor">Tor</option>
            <option value="cloud">Cloud</option>
            <option value="scanner">Scanner</option>
          </select>
        </div>

        {/* Bulk Actions Bar */}
        {selectedThreats.size > 0 && (
          <div className="bg-blue-500/10 border border-blue-500/30 rounded-lg p-4 flex items-center justify-between animate-slide-in-up">
            <div className="flex items-center gap-3">
              <CheckSquare className="w-5 h-5 text-blue-400" />
              <span className="text-white font-medium">{selectedThreats.size} threat{selectedThreats.size !== 1 ? 's' : ''} selected</span>
            </div>
            <div className="flex items-center gap-3">
              <button
                onClick={exportSelected}
                className="flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-500 text-white rounded-lg transition-colors"
              >
                <Download className="w-4 h-4" />
                Export Selected
              </button>
              <button
                onClick={clearSelection}
                className="flex items-center gap-2 px-4 py-2 bg-sg-card border border-sg-border hover:border-red-500/50 text-gray-300 hover:text-white rounded-lg transition-all"
              >
                <X className="w-4 h-4" />
                Clear
              </button>
            </div>
          </div>
        )}
      </div>

      {isLoading ? (
        viewMode === 'cards' ? (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <ThreatCardSkeleton />
            <ThreatCardSkeleton />
            <ThreatCardSkeleton />
            <ThreatCardSkeleton />
          </div>
        ) : (
          <div className="bg-sg-card border border-sg-border rounded-xl overflow-hidden">
            <table className="w-full">
              <thead>
                <tr className="border-b border-sg-border bg-gray-900">
                  <th className="text-left p-4 text-sm font-medium text-gray-400">Threat</th>
                  <th className="text-left p-4 text-sm font-medium text-gray-400">Source</th>
                  <th className="text-left p-4 text-sm font-medium text-gray-400">Primary Technique</th>
                  <th className="text-left p-4 text-sm font-medium text-gray-400">Details</th>
                  <th className="text-left p-4 text-sm font-medium text-gray-400">Time</th>
                </tr>
              </thead>
              <tbody>
                <TableRowSkeleton />
                <TableRowSkeleton />
                <TableRowSkeleton />
                <TableRowSkeleton />
              </tbody>
            </table>
          </div>
        )
      ) : filteredThreats.length === 0 && threats.length > 0 ? (
        <EmptyState
          icon={Shield}
          title="No Matching Threats"
          description="No threats match your current filters. Try adjusting your search criteria."
        />
      ) : filteredThreats.length === 0 ? (
        <EmptyState
          icon={Shield}
          title="No Active Threats"
          description="No active threats detected. Your systems are currently secure."
        />
      ) : viewMode === 'cards' ? (
        <ThreatCardView 
          threats={filteredThreats} 
          selectedThreats={selectedThreats}
          onToggleSelect={(id) => {
            const newSelected = new Set(selectedThreats);
            if (newSelected.has(id)) {
              newSelected.delete(id);
            } else {
              newSelected.add(id);
            }
            setSelectedThreats(newSelected);
          }}
          onSelectAll={toggleSelectAll}
          onViewDetails={(threat) => setDetailPanelThreat(threat)}
        />
      ) : (
        <ThreatTableView 
          threats={filteredThreats}
          selectedThreats={selectedThreats}
          onToggleSelect={(id) => {
            const newSelected = new Set(selectedThreats);
            if (newSelected.has(id)) {
              newSelected.delete(id);
            } else {
              newSelected.add(id);
            }
            setSelectedThreats(newSelected);
          }}
          onSelectAll={toggleSelectAll}
          onViewDetails={(threat) => setDetailPanelThreat(threat)}
        />
      )}

      {/* Detail Panel */}
      {detailPanelThreat && (
        <ThreatDetailPanel 
          threat={detailPanelThreat}
          onClose={() => setDetailPanelThreat(null)}
        />
      )}
    </div>
  );
}

// Card View Component
interface ThreatViewProps {
  threats: ThreatFeedItem[];
  selectedThreats: Set<string>;
  onToggleSelect: (id: string) => void;
  onSelectAll: () => void;
  onViewDetails: (threat: ThreatFeedItem) => void;
}

function ThreatCardView({ threats, selectedThreats, onToggleSelect, onSelectAll, onViewDetails }: ThreatViewProps) {
  const allSelected = threats.length > 0 && threats.every(t => selectedThreats.has(t.id));
  
  return (
    <div className="space-y-4">
      {/* Select All */}
      <div className="flex items-center gap-2">
        <button
          onClick={onSelectAll}
          className="flex items-center gap-2 px-3 py-2 bg-sg-card border border-sg-border rounded-lg hover:border-blue-500/50 transition-all text-sm"
        >
          {allSelected ? <CheckSquare className="w-4 h-4 text-blue-400" /> : <Square className="w-4 h-4" />}
          {allSelected ? 'Deselect All' : 'Select All'}
        </button>
        <span className="text-sm text-gray-500">{threats.length} threat{threats.length !== 1 ? 's' : ''}</span>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {threats.map((threat) => (
          <ThreatCard 
            key={threat.id} 
            threat={threat}
            isSelected={selectedThreats.has(threat.id)}
            onToggleSelect={onToggleSelect}
            onViewDetails={onViewDetails}
          />
        ))}
      </div>
    </div>
  );
}

// Individual Threat Card
interface ThreatCardProps {
  threat: ThreatFeedItem;
  isSelected: boolean;
  onToggleSelect: (id: string) => void;
  onViewDetails: (threat: ThreatFeedItem) => void;
}

function ThreatCard({ threat, isSelected, onToggleSelect, onViewDetails }: ThreatCardProps) {
  const [expanded, setExpanded] = useState(false);
  
  return (
    <div className={`bg-gray-900 border rounded-lg transition-all hover:shadow-lg hover:shadow-blue-500/10 hover:-translate-y-0.5 ${
      isSelected ? 'border-blue-500 ring-2 ring-blue-500/20' : 'border-gray-800 hover:border-blue-500/50'
    }`}>
      {/* Card Header */}
      <div className="p-4 border-b border-gray-800">
        <div className="flex items-start gap-3 mb-3">
          {/* Checkbox */}
          <button
            onClick={(e) => {
              e.stopPropagation();
              onToggleSelect(threat.id);
            }}
            className="mt-1 flex-shrink-0"
            aria-label={isSelected ? 'Deselect threat' : 'Select threat'}
          >
            {isSelected ? 
              <CheckSquare className="w-5 h-5 text-blue-400" /> : 
              <Square className="w-5 h-5 text-gray-600 hover:text-gray-400 transition-colors" />
            }
          </button>
          <div className="flex-1">
            <div className="flex items-center gap-3 mb-2">
              <ThreatBadge level={threat.level} />
              <span className="text-sm font-semibold text-white">{threat.confidence}% Confidence</span>
            </div>
            <div className="flex items-center gap-2 text-sm text-gray-400 flex-wrap">
              <Globe className="w-4 h-4" />
              <span className="font-mono">{threat.sourceIp}</span>
              {threat.sourceCountry && (
                <span className="px-2 py-0.5 bg-gray-800 rounded text-xs">
                  {threat.sourceCountry}
                </span>
              )}
              {/* Attribution Badges */}
              {threat.isTor && (
                <AttributionBadge type="tor" label="TOR" tooltip="Tor Exit Node" />
              )}
              {threat.isCloud && threat.cloudProvider && (
                <AttributionBadge type="cloud" label={threat.cloudProvider} tooltip={`Cloud Provider: ${threat.cloudProvider}`} />
              )}
              {threat.isKnownScanner && threat.scannerName && (
                <AttributionBadge type="scanner" label={threat.scannerName} tooltip={`Scanner: ${threat.scannerName}`} />
              )}
            </div>
          </div>
          <div className="text-right">
            <div className="flex items-center gap-1 text-xs text-gray-500 mb-1">
              <Clock className="w-3 h-3" />
              {safeDate(threat.timestamp).split(',')[1]?.trim() || safeDate(threat.timestamp)}
            </div>
            <div className="flex items-center gap-1 text-xs text-gray-500">
              <Activity className="w-3 h-3" />
              {threat.requestCount} requests
            </div>
          </div>
        </div>
      </div>

      {/* Attack Techniques */}
      <div className="p-4">
        <div className="text-xs text-gray-500 mb-2 font-medium">ATTACK TECHNIQUES</div>
        <div className="space-y-2">
          {threat.techniques.slice(0, expanded ? undefined : 3).map((techId) => {
            const category = getTechniqueCategory(techId);
            const colorClass = getTechniqueCategoryColor(category);
            return (
              <div key={techId} className={`p-2 rounded-lg border ${colorClass}`}>
                <div className="flex items-start justify-between gap-2">
                  <div className="flex-1 min-w-0">
                    <div className="font-medium text-sm mb-1">{getTechniqueName(techId)}</div>
                    <div className="text-xs opacity-75">{getTechniqueDescription(techId)}</div>
                  </div>
                  <code className="text-xs font-mono opacity-60 whitespace-nowrap">{techId}</code>
                </div>
              </div>
            );
          })}
        </div>
        
        {threat.techniques.length > 3 && (
          <button
            onClick={() => setExpanded(!expanded)}
            className="mt-2 text-xs text-blue-400 hover:text-blue-300 transition-all hover:translate-x-1"
          >
            {expanded ? '← Show less' : `Show ${threat.techniques.length - 3} more →`}
          </button>
        )}
      </div>

      {/* Additional Info */}
      {threat.deceptionEngaged && (
        <div className="px-4 pb-4">
          <div
            className="flex items-center gap-2 px-3 py-2 bg-green-500/10 border border-green-500/30 rounded-lg transition-all hover:bg-green-500/20"
            title={ENGAGEMENT_VOCAB.decoyTimeBurned.hint}
          >
            <Shield className="w-4 h-4 text-green-400 animate-pulse" />
            <span className="text-sm text-green-400 font-medium">{ENGAGEMENT_VOCAB.decoyTimeBurned.label}</span>
          </div>
        </div>
      )}

      {/* Footer */}
      <div className="px-4 pb-4 flex items-center justify-between text-xs">
        <span className="font-mono text-gray-600">Session: {threat.sessionId.slice(0, 12)}</span>
        <button
          onClick={() => onViewDetails(threat)}
          className="text-blue-400 hover:text-blue-300 font-medium transition-colors"
        >
          View Details →
        </button>
      </div>
    </div>
  );
}

// Table View Component
function ThreatTableView({ threats, selectedThreats, onToggleSelect, onSelectAll, onViewDetails }: ThreatViewProps) {
  const allSelected = threats.length > 0 && threats.every(t => selectedThreats.has(t.id));
  
  return (
    <div className="bg-sg-card border border-sg-border rounded-xl overflow-hidden">
      <table className="w-full">
        <thead>
          <tr className="border-b border-sg-border bg-gray-900">
            <th className="p-4">
              <button
                onClick={onSelectAll}
                aria-label={allSelected ? 'Deselect all' : 'Select all'}
              >
                {allSelected ? 
                  <CheckSquare className="w-5 h-5 text-blue-400" /> : 
                  <Square className="w-5 h-5 text-gray-600 hover:text-gray-400 transition-colors" />
                }
              </button>
            </th>
            <th className="text-left p-4 text-sm font-medium text-gray-400">Threat</th>
            <th className="text-left p-4 text-sm font-medium text-gray-400">SGIS</th>
            <th className="text-left p-4 text-sm font-medium text-gray-400">Source</th>
            <th className="text-left p-4 text-sm font-medium text-gray-400">Primary Technique</th>
            <th className="text-left p-4 text-sm font-medium text-gray-400">Details</th>
            <th className="text-left p-4 text-sm font-medium text-gray-400">Time</th>
            <th className="text-left p-4 text-sm font-medium text-gray-400">Actions</th>
          </tr>
        </thead>
        <tbody>
          {threats.map((threat) => {
            const primaryTech = threat.techniques[0];
            const category = primaryTech ? getTechniqueCategory(primaryTech) : '';
            const colorClass = getTechniqueCategoryColor(category);
            const isSelected = selectedThreats.has(threat.id);
            
            return (
              <tr 
                key={threat.id} 
                className={`border-b border-sg-border hover:bg-sg-dark/50 transition-colors ${
                  isSelected ? 'bg-blue-500/5' : ''
                }`}
              >
                <td className="p-4">
                  <button
                    onClick={() => onToggleSelect(threat.id)}
                    aria-label={isSelected ? 'Deselect threat' : 'Select threat'}
                  >
                    {isSelected ? 
                      <CheckSquare className="w-5 h-5 text-blue-400" /> : 
                      <Square className="w-5 h-5 text-gray-600 hover:text-gray-400 transition-colors" />
                    }
                  </button>
                </td>
                <td className="p-4">
                  <div className="flex items-center gap-3">
                    <ThreatBadge level={threat.level} />
                    <span className="text-sm font-semibold text-white">{threat.confidence}%</span>
                  </div>
                </td>
                <td className="p-4">
                  {threat.sgisScore !== undefined ? (
                    <SgisBadge score={threat.sgisScore} size="sm" showLabel={false} />
                  ) : (
                    <span className="text-xs text-gray-600 italic">N/A</span>
                  )}
                </td>
                <td className="p-4">
                  <div className="flex flex-col gap-1">
                    <span className="text-sm text-gray-300 font-mono">{threat.sourceIp}</span>
                    {threat.sourceCity && (
                      <span className="text-xs text-gray-500">{threat.sourceCity}, {threat.sourceCountry}</span>
                    )}
                    {/* Attribution badges */}
                    <div className="flex flex-wrap gap-1">
                      {threat.isTor && (
                        <AttributionBadge type="tor" label="TOR" tooltip="Tor Exit Node" />
                      )}
                      {threat.isCloud && threat.cloudProvider && (
                        <AttributionBadge type="cloud" label={threat.cloudProvider} tooltip={`Cloud: ${threat.cloudProvider}`} />
                      )}
                      {threat.isKnownScanner && threat.scannerName && (
                        <AttributionBadge type="scanner" label={threat.scannerName} tooltip={`Scanner: ${threat.scannerName}`} />
                      )}
                    </div>
                  </div>
                </td>
                <td className="p-4">
                  {primaryTech ? (
                    <div className={`inline-flex flex-col p-2 rounded-lg border ${colorClass} max-w-xs`}>
                      <span className="text-sm font-medium">{getTechniqueName(primaryTech)}</span>
                      <span className="text-xs opacity-75">{getTechniqueDescription(primaryTech)}</span>
                      <code className="text-xs font-mono opacity-60 mt-1">{primaryTech}</code>
                    </div>
                  ) : (
                    <span className="text-gray-500">-</span>
                  )}
                </td>
                <td className="p-4">
                  <div className="flex flex-col gap-1 text-xs">
                    <div className="text-gray-400">{threat.requestCount} requests</div>
                    {threat.techniques.length > 1 && (
                      <div className="text-gray-500">+{threat.techniques.length - 1} techniques</div>
                    )}
                    {threat.deceptionEngaged && (
                      <div
                        className="flex items-center gap-1 text-green-400"
                        title={ENGAGEMENT_VOCAB.decoyTimeBurned.hint}
                      >
                        <Shield className="w-3 h-3" />
                        {ENGAGEMENT_VOCAB.decoyTimeBurned.label}
                      </div>
                    )}
                  </div>
                </td>
                <td className="p-4 text-sm text-gray-500">
                  {safeDate(threat.timestamp).split(',')[1]?.trim() || safeDate(threat.timestamp)}
                </td>
                <td className="p-4">
                  <button
                    onClick={() => onViewDetails(threat)}
                    className="text-blue-400 hover:text-blue-300 font-medium text-sm transition-colors"
                  >
                    Details →
                  </button>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
